import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useConfirmationModal } from "@features/ConfirmationModal/useConfirmationModal";
import { Button } from "@components/Button";
import { Modal } from "@components/Modal/Modal";
import { useApiErrorUtils } from "@hooks/useApiErrorUtils";
import { useModal } from "@hooks/useModal";
import { type IUseModalProps } from "@store/modal/modal";
import { useDeactivateChubbBillInsuranceMutation } from "@services/insurance/mutations";
import { useChubbBillInsurancesQuery } from "@services/insurance/queries";
import { ChubbInsuranceModalAccordion } from "./components/ChubbInsuranceModalAccordion/ChubbInsuranceModalAccordion";

interface IChubbInsuranceModalProps extends IUseModalProps {
  openBillInsuranceActivationModal: (
    preselectedJointBillServiceId?: number,
  ) => void;
}

export const ChubbInsuranceModal: React.FC<IChubbInsuranceModalProps> = ({
  openBillInsuranceActivationModal,
  ...modalProps
}) => {
  const { t } = useTranslation();
  const modal = useModal(modalProps);

  const { toastApiError } = useApiErrorUtils();
  const { openConfirmationModal } = useConfirmationModal();

  const { data: chubbBillInsurancesData } = useChubbBillInsurancesQuery();
  const { mutateAsync: deactivateChubbInsurance } =
    useDeactivateChubbBillInsuranceMutation();

  const submitInsuranceDeactivation = useCallback(
    async (jointBillServiceId: number) => {
      await deactivateChubbInsurance(jointBillServiceId)
        .then(() => {
          toast.success(t("chubbInsuranceModal.toast.success"));
        })
        .catch(toastApiError);
    },
    [deactivateChubbInsurance, t, toastApiError],
  );

  const firstOrderedInsuranceJbsId =
    chubbBillInsurancesData?.jointBillService.find(
      (jbs) => jbs.insuranceStatus,
    )?.jointBillServiceId;

  return (
    <Modal
      modal={modal}
      title={t("chubbInsuranceModal.title")}
      subtitle={t("chubbInsuranceModal.subtitle")}
      className="md:max-w-3xl"
      actionButtons={
        <>
          <Button className="w-full" variant="secondary" onClick={modal.close}>
            {t("chubbInsuranceModal.actions.close")}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-6">
        {chubbBillInsurancesData?.jointBillService.map((data, index) => (
          <ChubbInsuranceModalAccordion
            insuranceData={data}
            isDefaultOpen={
              firstOrderedInsuranceJbsId
                ? firstOrderedInsuranceJbsId === data.jointBillServiceId
                : index === 0
            }
            onDeactivationButtonClick={() =>
              openConfirmationModal({
                onConfirm: () =>
                  submitInsuranceDeactivation(data.jointBillServiceId),
                confirmButtonLabel: t(
                  "chubbInsuranceModal.confirmationModal.actions.submit",
                ),
                children: (
                  <div className="mt-5 flex flex-col px-4 text-center text-sm">
                    <span className="mb-4 text-lg font-semibold">
                      {t("chubbInsuranceModal.confirmationModal.title")}
                    </span>
                    <span className="text-grey-dark">
                      {t("chubbInsuranceModal.confirmationModal.subtitle")}
                    </span>
                    <span className="font-semibold">
                      {data.displayName ??
                        (data.isFakeAddress
                          ? t("common.defaultAddress")
                          : data.address)}
                    </span>
                  </div>
                ),
              })
            }
            onActivationButtonClick={() =>
              openBillInsuranceActivationModal(data.jointBillServiceId)
            }
            key={index}
          />
        ))}
      </div>
    </Modal>
  );
};
